import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCheckCircle, faPlusCircle } from '@fortawesome/pro-light-svg-icons'

import classnames from 'classnames'

import { useSetState } from '@domain-group/react-hooks'

import { Box, LoadingBubbles, Text } from '@domain-group/suit-theme'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import useCampaignComparable from '@hooks/useCampaignComparable'

import { CampaignComparableModel, PropertySuggestionModel } from '@models/types'

import styles from './styles.module.scss'

const defaultState = {
  loading: false,
}

type SetState = (state: Partial<typeof defaultState>) => void

type PropertySuggestionProps = {
  added: boolean,
  callbacks: {
    createComparableFromDataProvider: (payload: HandleCallbackActionParams) => void,
  },
  campaignComparable: CampaignComparableModel,
  suggestion: PropertySuggestionModel,
}

type SelectPropertyParams = {
  callbacks: PropertySuggestionProps['callbacks'],
  createComparablePayload: HandleCallbackActionParams,
  setState: SetState,
}

const selectProperty = async (params: SelectPropertyParams) => {
  const { callbacks, createComparablePayload, setState } = params
  const { createComparableFromDataProvider } = callbacks

  setState({ loading: true })
  await createComparableFromDataProvider(createComparablePayload)
  setState({ loading: false })
}

const PropertySuggestion = (props: PropertySuggestionProps) => {
  const {
    added,
    callbacks,
    campaignComparable: initCampaignComparable,
    suggestion,
  } = props

  const [state, setState] = useSetState(defaultState)
  const { loading } = state

  const {
    address,
    data_provider,
    id,
    legal_description,
    post_code,
    state_name,
    suburb_name,
  } = suggestion

  const {
    callbacks: {
      createComparableFromDataProvider: createFn,
    },
    campaignComparable,
  } = useCampaignComparable(initCampaignComparable)

  const createComparablePayload = {
    callbacks: {
      action: createFn,
    },
    entityParams: {
      campaign_id: campaignComparable.campaign_id,
      data_provider,
      id,
    },
    requestOptions: {
      include_campaign_comparable_image: true,
      include_image_sizes: true,
    },
    toastText: 'Comparable Created',
  }

  return (
    <Box
      className={classnames(styles.root, added ? styles.added : styles.hoverStyle)}
      onClick={!added
        // @ts-expect-error TS2322
        ? () => selectProperty({ callbacks, createComparablePayload, setState })
        : undefined}
      marginBottom="large"
      padding="large"
      variant="white"
    >
      <Box flexDirection="column" minWidth="80%">
        <Text fontSize="small" marginBottom="small">{address}</Text>

        <Text
          color="bodyFontLightColor"
          fontSize="xsmall"
        >
          {suburb_name} {state_name} {post_code} {legal_description && `(${legal_description})`}
        </Text>
      </Box>

      <Box color="bodyFontLightColor" minWidth="20%" justifyContent="flex-end" overflow="hidden" margin="auto">
        <>
          {loading && <LoadingBubbles />}

          {!loading && (
            <FontAwesomeIcon
              className={added ? styles.checkIcon : styles.plusIcon}
              icon={added ? faCheckCircle : faPlusCircle}
            />
          )}
        </>
      </Box>
    </Box>
  )
}

export default PropertySuggestion
